import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Modal,
  Spin,
  message,
} from 'antd';
import NumericInput from 'components/NumericInput';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Table from 'components/Table';
import Select from 'components/Select';
import { ActionsNames, hasActionPermission } from 'utils/permission/index';
import { isMutable } from 'utils/estadosGlobales';
import {
  getLineasDeVehiculos,
  postLineaDeVehiculos,
  patchLineaDeVehiculo,
  deleteLineaDeVehiculo,
} from 'api/recaudacion/lineasVehiculares';
import { getMarcasDeVehiculos } from 'api/recaudacion';

const cruds = {
  vehiculos: {
    get: getLineasDeVehiculos,
    post: postLineaDeVehiculos,
    patch: patchLineaDeVehiculo,
    delete: deleteLineaDeVehiculo,
    marcasGetter: getMarcasDeVehiculos,
    permisisonModels: ['marcadelvehiculo'],
  },
};

// eslint-disable-next-line react/prop-types
const Lineas = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ results: [], count: 0 });
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [mutable, setMutable] = useState(true);
  const currentURI = window.location.href.split('-').pop();
  const {
    get,
    post,
    patch,
    delete: _delete,
    marcasGetter,
    permisisonModels,
  } = cruds[currentURI];

  const [marcaTemp, setMarcaTemp] = useState({ results: [], count: 0 });
  const marcaRef = useRef({ results: [] });

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const res = await get({ page, ...q });
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = permisisonModels
        .map((m) => hasActionPermission(m.trim(), ActionsNames.view))
        .every((e) => e);
      hasAllPermission = _hasAllViewPermission;
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const _data = await get({ page: 1 });
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    if (hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onFinish = async () => {
    setLoading(true);
    await form.validateFields();
    const values = form.getFieldsValue();
    const [key] = selectedRowKeys;
    const saved = await (key ? patch(key, values) : post(values));
    if (saved) {
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await _delete(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    const match = data.results.find((e) => e.id === key);
    if (match?.marca) {
      const res = await marcasGetter({ page: 1, id: match.marca });
      setMarcaTemp(res);
    }
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 550,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
        use: true,
      },
    },
  ];

  const rules = {
    required: [{ required: true }],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        <Table
          cols={columns}
          data={data?.results || []}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          rowKey="id"
          permission={permission}
          hasAllViewPermission={hasAllViewPermission}
          pagination={false}
          count={data.count}
          paginationCallback={fetchData}
          currentPage={currentPage}

        />
        <Modal
          visible={visible}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Línea`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          footer={null}
          closable={false}
          keyboard={!loading}
          maskClosable={!loading}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{ estados_globales: 1 }}
          >
            <Row>
              <Col span={24}>
                <NumericInput
                  name="clave"
                  label="Clave"
                  disabled={!!selectedRowKeys.length || !mutable}
                  required
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  name="nombre"
                  label="Nombre"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input allowClear disabled={!mutable} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="marca"
                  label="Marca"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    onClear={() => {
                      setMarcaTemp({ results: [] });
                    }}
                    labelProp="nombre"
                    usingExternalDataSource={Boolean(marcaTemp.results.length)}
                    dataSource={marcaTemp.results.length
                      ? marcaTemp : marcaRef.current}
                    pagination={{
                      getter: marcasGetter,
                      setter: (dataSource) => {
                        marcaRef.current = dataSource;
                      },
                      config: {
                        qName: 'nombre',
                        auto: true,
                      },
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  rules={rules.required}
                  name="estados_globales"
                  label="Estado"
                  hasFeedback
                >
                  <Select
                    disabled={!selectedRowKeys.length || !mutable}
                    dataSource={estadosGlobales}
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={`Linea ${form.getFieldValue('nombre')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Lineas;
